// Imports
import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import LoadingRow from '../../../../../../shareables/foundation/front-end/components/tables/loading-row';
import { useAppDispatch, useAppSelector } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetPaymentOptionsResponse } from '../../../../../../shareables/types/api/main/website/payment-options/get';
import planMapping from '../../../../../../shareables/utils/billing/plan-mapping';
import Table from '../../page-types/shared-components/table';
import PageTitle from '../../shared-components/page-title';
import UnavailableFeature from '../../unavailable-feature';
import PaymentOptionRow from './row';


// Styled components
const NoPaymentOptions = styled.td`
	background-color: #f9f9f9;
`;


// Define the accepted props
interface Props {
	storybookPaymentOptions?: GetPaymentOptionsResponse;
}


// Function component
const PaymentOptions: React.FC<Props> = ({ storybookPaymentOptions }) => {
	// Use state
	const [paymentOptions, setPaymentOptions] = useState<GetPaymentOptionsResponse | null>(
		storybookPaymentOptions ?? null
	);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const websitePlan = useAppSelector((state) => state.self.website?.plan);
	
	
	// Initialize
	const insufficientPlan = (websitePlan ?? 0) < planMapping.deluxe;
	
	
	// Load payment options on mount
	useEffect(() => {
		// Return early
		if (insufficientPlan) {
			return;
		}
		
		
		// Call API
		void dispatch(
			apiActions.call<undefined, undefined, GetPaymentOptionsResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/payment-options`,
				completion: (json) => {
					setPaymentOptions(json.response);
				},
			})
		);
	}, [dispatch, insufficientPlan]);
	
	
	// Handle if website doesn't have access to the required plan
	if (insufficientPlan) {
		return <UnavailableFeature />;
	}
	
	
	// Return JSX
	return (
		<React.Fragment>
			<PageTitle>Online payments</PageTitle>
			
			<Table>
				<thead>
					<tr>
						<th style={{ textAlign: 'left' }}>Title</th>
						<th style={{ textAlign: 'center' }}>Pay</th>
					</tr>
				</thead>
				
				<tbody>
					{paymentOptions?.map((paymentOption) => (
						<PaymentOptionRow key={paymentOption.id} paymentOption={paymentOption}></PaymentOptionRow>
					))}
					
					{paymentOptions?.length === 0 && (
						<tr>
							<NoPaymentOptions colSpan={999} style={{ textAlign: 'left' }}>
								<em>No online payment options available at this time…</em>
							</NoPaymentOptions>
						</tr>
					)}
					
					{!paymentOptions && <LoadingRow />}
				</tbody>
			</Table>
		</React.Fragment>
	);
};

export default PaymentOptions;
